import type {SketchGroup} from '../../mesh';
import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import {
    SketchClosureCandidateFilter,
    SketchClosureTopologyBuilder,
} from '../closure';
import type {
    SketchOffsetChainClosedProfileInput,
    SketchOffsetClosedProfileFinalizeInput,
    SketchOffsetClosedProfileFinalizeResult,
} from '../../../types/sketch/offset/SketchOffsetClosedProfileFinalizer';

export type {
    SketchOffsetChainClosedProfileInput,
    SketchOffsetClosedProfileFinalizeInput,
    SketchOffsetClosedProfileFinalizeResult,
} from '../../../types/sketch/offset/SketchOffsetClosedProfileFinalizer';

/**
 * 等距实体闭合轮廓收口器。
 *
 * 说明：
 * 1. 等距实体（含顶端加盖）落地后的多实体闭合环统一通过
 *    `SketchGroup.tryFinalizeClosedSketchCollection` 收口，与绘制、裁剪、
 *    转换实体引用共用同一闭合解析链路，不在等距域重复实现闭合拓扑算法；
 * 2. 收口语义与转换实体引用收口器一致：上色草图轮廓开关只影响轮廓显示对象，
 *    不影响闭合 profile 语义（开关关闭时仍创建 `SketchCollectionObject`，
 *    供拉伸等特征绑定消费）；
 * 3. 种子构成按链组合规则推导：
 *    - 单向 + 顶端加盖：源链 + 偏移链 + 盖子（盖连接偏移自由端 ↔ 源自由端）；
 *    - 双向 + 顶端加盖：左右偏移链 + 盖子（盖连接左右偏移链端点，源链不参与）；
 *    - 无盖（闭合源链）：偏移链自身成环，逐链收口；
 *    - 双向无盖：左右偏移链各自成环，分别收口；
 * 4. 构造线（基本几何体/偏移几何体选项）由闭合候选过滤器统一拒绝，不建集合，
 *    与 SolidWorks 构造线不参与上色轮廓的语义一致。
 */
export class SketchOffsetClosedProfileFinalizer {
    /** 草图闭合候选过滤器（与草图组闭合入口同一套候选规则）。 */
    private readonly closureCandidateFilter = new SketchClosureCandidateFilter();

    /** 闭合拓扑构建器（种子边首尾相接排序复用闭合模块同一端点键口径）。 */
    private readonly topologyBuilder = new SketchClosureTopologyBuilder();

    /**
     * 对本次落地的等距链执行闭合轮廓收口。
     *
     * @param input 收口输入。
     * @returns 收口结果（本次创建的闭合集合持久 ID）。
     */
    public finalize(input: SketchOffsetClosedProfileFinalizeInput): SketchOffsetClosedProfileFinalizeResult {
        const createdCollectionIds: string[] = [];

        // 按源链组合键分组：双向偏移对同一条源链登记左右两条记录，必须合并成一次收口
        // （盖子被首个闭合集合锁定后，第二条链单独收口必然失败）
        for (const group of this.groupChainsBySource(input.chains)) {
            for (const seedIds of this.resolveGroupSeedIdBatches(group)) {
                const seeds = this.resolveSeedSources(input.sketchGroup, seedIds);
                if (!seeds) {
                    continue;
                }
                // 闭合解析要求种子边按首尾相接顺序排列（resolveSequentialTraversalSteps
                // 逐条校验前一条尾端点键 === 后一条起端点键），落地记录的 ID 顺序
                // （源链、偏移链、盖子）不满足该前提，必须先做拓扑排序
                const orderedSeeds = this.orderSequentialSeedSources(seeds);
                if (!orderedSeeds) {
                    continue;
                }
                const collection = input.sketchGroup.tryFinalizeClosedSketchCollection(
                    orderedSeeds,
                    {syncContour: input.syncContour},
                );
                if (collection) {
                    createdCollectionIds.push(collection.persistentId);
                }
            }
        }

        return {createdCollectionIds};
    }

    /**
     * 按源链持久 ID 组合键对落地链分组。
     *
     * @param chains 本次落地的等距链列表。
     * @returns 分组后的链列表（双向偏移的左右链在同一组）。
     */
    private groupChainsBySource(
        chains: readonly SketchOffsetChainClosedProfileInput[],
    ): SketchOffsetChainClosedProfileInput[][] {
        const groups = new Map<string, SketchOffsetChainClosedProfileInput[]>();
        for (const chain of chains) {
            const key = chain.sourceEntityIds.join('|');
            const group = groups.get(key) ?? [];
            group.push(chain);
            groups.set(key, group);
        }
        return [...groups.values()];
    }

    /**
     * 解析单组链需要收口的种子持久 ID 批次。
     *
     * 说明：
     * 1. 有盖时整组合并为一批（单向含源链；双向含左右偏移链）；
     * 2. 无盖时（闭合源链）每条链的偏移链各自成环，逐链一批。
     *
     * @param group 同源链的落地链组。
     * @returns 种子持久 ID 批次列表。
     */
    private resolveGroupSeedIdBatches(
        group: readonly SketchOffsetChainClosedProfileInput[],
    ): readonly (readonly string[])[] {
        // 去重合并组内全部盖子 ID（双向时左右链记录携带同一批盖子）
        const capEntityIds = this.uniqueIds(group.flatMap((chain) => chain.capEntityIds));

        if (capEntityIds.length > 0) {
            // 单向：盖连接偏移自由端 ↔ 源自由端，源链参与闭环；
            // 双向：盖连接左右偏移链端点，源链不参与。
            const offsetEntityIds = this.uniqueIds(group.flatMap((chain) => chain.offsetEntityIds));
            if (group.length === 1) {
                const chain = group[0]!;
                return [[...chain.sourceEntityIds, ...offsetEntityIds, ...capEntityIds]];
            }
            return [[...offsetEntityIds, ...capEntityIds]];
        }

        // 无盖：闭合源链的偏移链自身成环，逐链独立收口
        return group.map((chain) => chain.offsetEntityIds);
    }

    /**
     * 按持久 ID 在草图组内解析种子边。
     *
     * 说明：任一条记录无法完整解析时整批跳过（与等距链派生同步服务
     * “记录无法完整解析则保留当前形状”的防御口径一致），避免残缺种子
     * 被闭合解析器误连成不完整轮廓。
     *
     * @param sketchGroup 目标草图组。
     * @param seedIds 种子持久 ID 列表。
     * @returns 解析成功的种子边；任一条解析失败返回 null。
     */
    private resolveSeedSources(
        sketchGroup: SketchGroup,
        seedIds: readonly string[],
    ): CollectSource[] | null {
        const seeds: CollectSource[] = [];
        for (const id of seedIds) {
            const object = sketchGroup.getObjectByPersistentId(id);
            if (!this.closureCandidateFilter.isCollectSourceForSketchClosure(object)) {
                return null;
            }
            seeds.push(object);
        }
        return seeds;
    }

    /**
     * 按端点拓扑键对种子边做首尾相接排序。
     *
     * 说明：
     * 1. `SketchClosureResolver.resolveSequentialTraversalSteps` 按传入顺序逐条校验
     *    “前一条尾端点键 === 后一条起端点键”，因此种子必须按闭合遍历顺序排列；
     * 2. 落地记录的 ID 顺序是「源链、偏移链、盖子」的业务顺序，不是几何遍历顺序
     *    （如单向盖子的遍历顺序是 源链 → 尾盖 → 偏移链反向 → 首盖），必须重排；
     * 3. 排序复用 `SketchClosureTopologyBuilder` 的端点键（共享点对象优先、
     *    坐标容差兜底），保证与后续闭合判定口径完全一致；
     * 4. 以深度优先回溯遍历全部排列候选，种子边数很少（链 + 两个盖），开销可忽略。
     *
     * @param seeds 待排序种子边。
     * @returns 首尾相接的种子边序列；无法排序时返回 null。
     */
    private orderSequentialSeedSources(seeds: readonly CollectSource[]): CollectSource[] | null {
        if (seeds.length === 0) {
            return null;
        }

        const graph = this.topologyBuilder.build(seeds);
        const ordered: CollectSource[] = [];

        // 深度优先回溯：从当前尾端点键出发，尝试所有未使用的候选边，
        // 候选边只需存在任一方向半边与尾端点键相接（方向由后续闭合解析自行确定）
        const walk = (currentEndKey: string, remaining: CollectSource[]): boolean => {
            if (remaining.length === 0) {
                return true;
            }
            for (let index = 0; index < remaining.length; index++) {
                const candidate = remaining[index]!;
                const halfEdges = graph.halfEdgesBySourceId.get(candidate.uuid) ?? [];
                for (const halfEdge of halfEdges) {
                    if (halfEdge.startKey !== currentEndKey) {
                        continue;
                    }
                    ordered.push(candidate);
                    const nextRemaining = remaining.filter((_, remainingIndex) => remainingIndex !== index);
                    if (walk(halfEdge.endKey, nextRemaining)) {
                        return true;
                    }
                    ordered.pop();
                }
            }
            return false;
        };

        const firstSource = seeds[0]!;
        const firstHalfEdges = graph.halfEdgesBySourceId.get(firstSource.uuid) ?? [];
        for (const firstHalfEdge of firstHalfEdges) {
            ordered.push(firstSource);
            if (walk(firstHalfEdge.endKey, seeds.slice(1))) {
                return [...ordered];
            }
            ordered.pop();
        }
        return null;
    }

    /**
     * 对持久 ID 列表去重并保留首次出现顺序。
     *
     * @param ids 原始持久 ID 列表。
     * @returns 去重后的持久 ID 列表。
     */
    private uniqueIds(ids: readonly string[]): string[] {
        return [...new Set(ids)];
    }
}
